import { NextPage } from 'next';
import React, { FC } from 'react';
import { IntlShape, useIntl } from 'react-intl';
import JsonLd from '../../../components/layouts/JsonLd';
import MetaTags from '../../../components/layouts/MetaTags';
import MerchantProductsPage from '../../../templates/merchants/products/IndexTemplate/MerchantProductsPage';
import { getCategory } from '../../../infrastructure/api/backend/explore_api';
import { CategoryEntity } from '../../../interfaces/explore/CategoryEntity';
import { MerchantDetail } from '../../../interfaces/merchants';
import { MerchantProduct } from '../../../interfaces/merchants/products';
import {
  fetchMerchantDetail,
  fetchMerchantProducts,
} from '../../../infrastructure/api';
import { injectAppContextGetServerSideProps } from '../../_app';
import ErrorTemplate from '../../../templates/ErrorTemplate';
import { MerchantSite } from '../../../components/MerchantSite';
import { usePushMerchantDataLayerEffect } from '../../../hooks/gtm';
import { buildSiteUrl } from 'src/utils/request-url';

const titleBy = (intl: IntlShape, displayName: string, currentPage: number) => {
  const base = intl.formatMessage(
    { id: 'merchants.products.index.meta.title' },
    { displayName },
  );
  if (currentPage <= 1) {
    return base;
  }
  return `${base} ${intl.formatMessage(
    { id: 'merchants.products.index.page' },
    { currentPage },
  )}`;
};

interface Props {
  pagination: {
    total_size: number;
    current_page: number;
  };
  products: MerchantProduct[];
  public_id: string;
  merchantDetail?: MerchantDetail;
  category?: CategoryEntity;
  isUnpublishedSite: boolean;
  isCoubicApp: boolean;
}

const MerchantProductComponent: FC<Props> = ({
  merchantDetail,
  category,
  products,
  pagination,
  isCoubicApp,
}) => {
  const intl = useIntl();
  usePushMerchantDataLayerEffect({ visit_merchant_id: merchantDetail?.id });
  if (merchantDetail && category) {
    const title = titleBy(
      intl,
      merchantDetail.display_name,
      pagination.current_page,
    );

    return (
      <>
        <MetaTags
          title={title}
          description={
            merchantDetail.seo_setting?.description ||
            merchantDetail.description
          }
          keywords={merchantDetail.seo_setting?.keyword || ''}
          thumbnailUrl={merchantDetail.cover_image.url || ''}
          currentPath={buildSiteUrl(
            `/products?page=${pagination.current_page}`,
          )}
          noIndex={merchantDetail.seo_setting?.no_index}
          googleAnalyticsFourTrackingId={
            merchantDetail.available_google_analytics_four_tracking_id
          }
          faviconImageUrl={merchantDetail?.favicon_image?.url}
        />
        <JsonLd merchant={merchantDetail} />
        <MerchantProductsPage
          pagination={pagination}
          products={products}
          merchantDetail={merchantDetail}
          category={category}
          isCoubicApp={isCoubicApp}
        />
      </>
    );
  }

  return <ErrorTemplate statusCode={404} />;
};

const MerchantProducts: NextPage<Props> = (props: Props) => {
  const { merchantDetail, isUnpublishedSite } = props;
  return (
    <MerchantSite
      isUnPublished={isUnpublishedSite}
      isMerchantInfoExist={!!merchantDetail}
      logoImage={merchantDetail?.logo_image || null}
      merchantPublicId={merchantDetail?.public_id || null}
      displayName={merchantDetail?.display_name || null}
      isCoubicApp={props.isCoubicApp}
    >
      <MerchantProductComponent {...props} />
    </MerchantSite>
  );
};

export const getServerSideProps = injectAppContextGetServerSideProps<Props>(
  async (ctx) => {
    const { query, session, token, requestId, isCoubicApp } = ctx;
    const page = query.page ? Number(query.page) : 1;
    const merchantPublicId = query.publicId ? String(query.publicId) : '1';

    const merchantDetailResponse = await fetchMerchantDetail({
      session,
      token,
      requestId,
      merchantPublicId,
      serverResponse: ctx.res,
    });
    if (merchantDetailResponse.redirect) {
      return { redirect: merchantDetailResponse.redirect };
    }
    const { merchant, isUnpublishedSite } = merchantDetailResponse;

    if (isUnpublishedSite) {
      return {
        props: {
          categories: [],
          pagination: {
            total_size: 0,
            current_page: 1,
          },
          products: [],
          public_id: merchantPublicId,
          merchantDetail: merchant,
          isUnpublishedSite,
          isCoubicApp,
        },
      };
    }

    const [products] = await Promise.all([
      // Layoutで取得している部分を徐々に置き換え
      fetchMerchantProducts({
        session,
        token,
        requestId,
        merchantPublicId,
        page,
        perPage: 10,
      }),
    ]);

    const category = merchant
      ? await getCategory(merchant.category_id)
      : undefined;
    const props: Props = {
      category,
      pagination: {
        total_size: products.total_size,
        current_page: products.current_page,
      },
      products: products.data,
      public_id: merchantPublicId,
      merchantDetail: merchant,
      isUnpublishedSite,
      isCoubicApp,
    };

    return { props };
  },
);

export default MerchantProducts;
